import React, { useEffect, useRef, useState } from 'react';
import { profile, stats } from '../data/profile';
import { Button } from './ui/Button';
import { CountUp } from '../lib/motion';
import { Icon } from '../lib/Icon';

/* Name that reveals letter-by-letter and lifts each glyph on hover. */
function KineticName({ text }) {
  return (
    <h1 style={{ fontSize: 'var(--text-hero)', lineHeight: 0.92, letterSpacing: 'var(--tracking-tighter)', margin: 0 }}>
      {text.split(' ').map((word, wi) => (
        <span key={wi} style={{ display: 'inline-block', whiteSpace: 'nowrap', marginRight: '0.18em' }}>
          {word.split('').map((ch, ci) => (
            <span key={ci} className="kinetic-letter" style={{
              display: 'inline-block',
              animation: 'ba-fade-up .8s var(--ease-out) both',
              animationDelay: `${0.15 + (wi * 5 + ci) * 0.04}s`,
              transition: 'transform .3s var(--ease-spring), color .3s var(--ease-out)',
              color: wi === 1 ? 'var(--accent)' : 'var(--text-primary)',
            }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-0.12em)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}>
              {ch}
            </span>
          ))}
        </span>
      ))}
    </h1>
  );
}

function RotatingRole({ roles }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % roles.length), 2600);
    return () => clearInterval(t);
  }, [roles.length]);
  return (
    <span style={{ position: 'relative', display: 'inline-block', color: 'var(--accent)' }}>
      {roles.map((r, idx) => (
        <span key={idx} style={{
          position: idx === i ? 'relative' : 'absolute', left: 0, top: 0, whiteSpace: 'nowrap',
          opacity: idx === i ? 1 : 0, transform: idx === i ? 'translateY(0)' : 'translateY(0.5em)',
          transition: 'opacity .5s var(--ease-out), transform .5s var(--ease-out)' }}>{r}</span>
      ))}
    </span>
  );
}

function Portrait({ src }) {
  const wrap = useRef(null);
  useEffect(() => {
    const el = wrap.current;
    if (!el || !window.matchMedia('(pointer:fine)').matches) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const px = (e.clientX - (r.left + r.width / 2)) / window.innerWidth;
      const py = (e.clientY - (r.top + r.height / 2)) / window.innerHeight;
      el.style.transform = `translate(${px * 24}px, ${py * 24}px)`;
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);
  return (
    <div ref={wrap} style={{ position: 'relative', width: 'min(38vw, 420px)', aspectRatio: '1', transition: 'transform .6s var(--ease-out)', willChange: 'transform' }}>
      <div aria-hidden="true" style={{ position: 'absolute', inset: '-12%', borderRadius: '50%', border: '1.5px dashed var(--yellow-a32)', animation: 'ba-spin 28s linear infinite' }} />
      <div aria-hidden="true" style={{ position: 'absolute', inset: '-2%', borderRadius: '50%', background: 'radial-gradient(circle, var(--yellow-a32), transparent 70%)', filter: 'blur(20px)' }} />
      <img src={src} alt="Bernardo Assad" style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
        borderRadius: '50%', border: '2px solid var(--border-default)',
        filter: 'grayscale(0.15) contrast(1.05)', boxShadow: 'var(--shadow-xl)' }} />
      <span style={{
        position: 'absolute', bottom: '6%', right: '-4%', display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '8px 14px', borderRadius: 'var(--radius-pill)', background: 'var(--ink-850)',
        border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-lg)',
        fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-secondary)' }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--positive)', boxShadow: '0 0 8px var(--positive)' }} />
        Disponível
      </span>
    </div>
  );
}

export default function Hero() {
  return (
    <section id="top" style={{
      position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center',
      padding: '120px clamp(20px,5vw,56px) 80px', overflow: 'hidden' }}>
      {/* dot grid */}
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0,
        backgroundImage: 'radial-gradient(var(--grid-line) 1px, transparent 1px)', backgroundSize: '32px 32px',
        maskImage: 'radial-gradient(120% 90% at 30% 30%, #000, transparent 75%)',
        WebkitMaskImage: 'radial-gradient(120% 90% at 30% 30%, #000, transparent 75%)' }} />
      <div className="hero-grid" style={{ position: 'relative', width: '100%', maxWidth: 'var(--container-full)', margin: '0 auto',
        display: 'grid', gap: 'clamp(40px,6vw,80px)', alignItems: 'center' }}>
        <div style={{ maxWidth: 720 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 28, animation: 'ba-fade-in .8s var(--ease-out) both' }}>
            <span style={{ width: 28, height: 1, background: 'var(--accent)' }} />
            <span className="t-eyebrow">Tech Lead em IA · São Paulo</span>
          </div>
          <KineticName text="Bernardo Assad" />
          <div style={{ marginTop: 24, fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 'clamp(1.4rem,3vw,2.2rem)', letterSpacing: '-0.02em', animation: 'ba-fade-up .8s var(--ease-out) .5s both' }}>
            <RotatingRole roles={profile.roles} />
          </div>
          <p style={{ marginTop: 24, maxWidth: 540, fontSize: 'var(--text-lg)', lineHeight: 1.6, color: 'var(--text-secondary)', animation: 'ba-fade-up .8s var(--ease-out) .65s both' }}>
            {profile.tagline}
          </p>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 38, animation: 'ba-fade-up .8s var(--ease-out) .8s both' }}>
            <Button variant="solid" size="lg" as="a" href="#projetos" iconRight={<Icon name="arrow-down-right" size={18} />}>Ver projetos</Button>
            <Button variant="outline" size="lg" as="a" href="#sobre">Sobre mim</Button>
          </div>
          <div style={{ display: 'flex', gap: 'clamp(20px,4vw,48px)', marginTop: 56, flexWrap: 'wrap', animation: 'ba-fade-up .8s var(--ease-out) .95s both' }}>
            {stats.map((s) => (
              <div key={s.label}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(1.6rem,3vw,2.4rem)', letterSpacing: '-0.03em', color: 'var(--accent)' }}><CountUp value={s.value} /></div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="hero-portrait" style={{ justifySelf: 'center' }}>
          <Portrait src={profile.photo} />
        </div>
      </div>
      {/* scroll cue */}
      <a href="#sobre" aria-label="Rolar" style={{ position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase' }}>Scroll</span>
        <span style={{ width: 22, height: 34, borderRadius: 12, border: '1.5px solid var(--border-strong)', position: 'relative' }}>
          <span style={{ position: 'absolute', top: 6, left: '50%', marginLeft: -2, width: 4, height: 8, borderRadius: 2, background: 'var(--accent)', animation: 'ba-scrollcue 1.6s var(--ease-out) infinite' }} />
        </span>
      </a>
    </section>
  );
}
